import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from '../tracker/hooks';
import type { SupabaseClient } from '../../lib/supabase';
import type { Deal } from './types';

/**
 * Hanneza's two-item checklist on every deal (9 September):
 *
 *     [ ] Added on LinkedIn
 *     [ ] Updated HubSpot (Associated Contacts, Associated Company, Deal
 *         Stage, Amount)
 *
 * Two items, two different kinds of thing, and the difference decides where
 * each one lives.
 *
 * "Added on LinkedIn" is an ACTION ON A PERSON. It is answered by a
 * `linkedin_added` activity on the deal's contact — the same row the
 * Prospecting tab writes when a prospect is marked Connected, so a deal whose
 * contact came up through prospecting is already ticked, which is exactly
 * why Nez wanted it ("not all leads come from LinkedIn"). Ticking it here
 * logs that activity through log_lead_activity: counted, attributed, on the
 * timeline. It is one-way. A tick is a statement that something happened;
 * taking it back is removing a timeline entry, which is an admin action on
 * the lead, not a checkbox.
 *
 * "Updated HubSpot" is ADMIN HYGIENE ON THE DEAL: `deals.hubspot_updated`, a
 * boolean, both ways. It must never write lead_activity — that would raise a
 * touch count for a person nobody spoke to and corrupt the round arithmetic.
 * It is counted in the actions funnel on its own line, never in Done.
 */

export type LinkedInTick =
  /** The contact has a linkedin_added activity. */
  | 'done'
  /** There is a contact and nothing says they were added. */
  | 'todo'
  /** No contact on the deal, so there is nobody to add. */
  | 'no_contact';

export interface DealChecklist {
  linkedin: LinkedInTick;
  hubspot: boolean;
  /** Both answered, or the LinkedIn half unanswerable. Nothing to chip. */
  complete: boolean;
}

export function checklistFor(
  deal: Pick<Deal, 'lead_id' | 'hubspot_updated'>,
  addedLeadIds: ReadonlySet<string>,
): DealChecklist {
  const linkedin: LinkedInTick =
    deal.lead_id === null ? 'no_contact' : addedLeadIds.has(deal.lead_id) ? 'done' : 'todo';
  return {
    linkedin,
    hubspot: deal.hubspot_updated,
    complete: linkedin !== 'todo' && deal.hubspot_updated,
  };
}

export const CHECKLIST_LABELS = {
  linkedin: 'Added on LinkedIn',
  linkedinNoContact: 'Added on LinkedIn (pick a contact first)',
  hubspot: 'Updated HubSpot (associated contacts, associated company, deal stage, amount)',
} as const;

/** What the card says while an item is open. Short: the card is 6.5rem. */
export const CHECKLIST_CHIPS = {
  linkedin: { glyph: 'in', label: 'Not yet added on LinkedIn' },
  hubspot: { glyph: 'HS', label: 'HubSpot not updated' },
} as const;

/**
 * Every lead somebody has recorded adding on LinkedIn.
 *
 * One small query for the whole board rather than one per card: the kind
 * is rare and the set is the thing the cards need. Keyed under the tracker
 * so logging an activity (which invalidates trackerKeys.leads) refreshes it.
 */
export async function fetchLinkedInAddedLeads(client: SupabaseClient): Promise<Set<string>> {
  const { data, error } = await client
    .from('lead_activity')
    .select('lead_id')
    .eq('kind', 'linkedin_added')
    .not('lead_id', 'is', null);
  if (error) {
    throw new Error(error.message);
  }
  const ids = new Set<string>();
  for (const row of Array.isArray(data) ? data : []) {
    if (row.lead_id !== null) ids.add(row.lead_id);
  }
  return ids;
}

export const linkedInAddedKey = () => [...trackerKeys.leads(), 'linkedin-added'] as const;

export function useLinkedInAddedLeads() {
  const { client } = useAuth();
  return useQuery({
    queryKey: linkedInAddedKey(),
    queryFn: () => fetchLinkedInAddedLeads(client),
    retry: false,
  });
}
